import { keyframes } from '@emotion/react';
import { Box, OptimizeImage, Stack, Text } from '@inflearn/ds-react';
import { CDN_BASE_PATH, UI_IMAGES } from './itemData';
import type { SelectedItems } from './types';

// 스페이스바 강조 애니메이션
const spacebarPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
`;

const spacebarGlow = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 8px rgba(255, 215, 0, 0.4));
  }
  50% {
    filter: drop-shadow(0 0 16px rgba(255, 215, 0, 0.7));
  }
`;

interface CharacterPreviewProps {
  selectedItems: SelectedItems;
  scale?: number;
  hideBorder?: boolean;
}

export const CharacterPreview = ({
  selectedItems,
  scale = 1,
  hideBorder = false,
}: CharacterPreviewProps) => {
  // 레이어 순서대로 정렬된 아이템들
  const layers = [
    selectedItems.background,
    selectedItems.top,
    selectedItems.expression,
    selectedItems.hat,
    selectedItems.effect,
  ].filter(Boolean);

  const hasAnyItem = layers.length > 0;

  const baseWidth = 154;
  const baseHeight = 148;
  const scaledWidth = baseWidth * scale;
  const scaledHeight = baseHeight * scale;

  return (
    <Stack spacing={16} align="center">
      {!hideBorder && (
        <Box
          pos="absolute"
          top={0}
          left={0}
          css={{
            width: `calc(100% + 1px)`,
            height: `calc(100% + 1px)`,
            borderRadius: '1px',
            border: '4px solid #B54A08',
            marginRight: '-2px',
            zIndex: 10,
          }}
        />
      )}

      <Box
        style={{
          position: 'relative',
          width: `${scaledWidth}px`,
          height: `${scaledHeight}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          boxShadow: hasAnyItem
            ? '0 10px 30px rgba(168, 85, 247, 0.2)'
            : '0 4px 12px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
        }}
      >
        {hasAnyItem ? (
          <Box
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* 레이어별로 쌓인 이모지들 */}
            {layers.map((item) => {
              if (!item) {
                return null;
              }

              // 카테고리별로 다른 크기 설정 (위치는 모두 동일)
              const getItemStyle = () => {
                const baseStyle = {
                  position: 'absolute' as const,
                  top: 0,
                  left: 0,
                  width: `${scaledWidth}px`,
                  height: `${scaledHeight}px`,
                };

                switch (item.category) {
                  case 'background':
                    return baseStyle;
                  case 'expression':
                    return baseStyle;
                  case 'top':
                    return baseStyle;
                  case 'hat':
                    return baseStyle;
                  case 'effect':
                    return {
                      ...baseStyle,
                      animation: 'float 2s ease-in-out infinite',
                    };
                  default:
                    return {};
                }
              };

              return (
                <Box key={item.id} style={getItemStyle()}>
                  {item.emoji.startsWith(CDN_BASE_PATH) ? (
                    <img
                      src={item.emoji}
                      alt={item.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <Text
                      style={{
                        fontSize:
                          item.category === 'expression'
                            ? `${96 * scale}px`
                            : item.category === 'hat'
                              ? `${72 * scale}px`
                              : item.category === 'top'
                                ? `${64 * scale}px`
                                : `${48 * scale}px`,
                        lineHeight: 1,
                      }}
                    >
                      {item.emoji}
                    </Text>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box
            css={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: `${spacebarPulse} 0.8s ease-in-out infinite`,
            }}
          >
            <OptimizeImage
              src={UI_IMAGES.SPACEBAR.src}
              w={UI_IMAGES.SPACEBAR.w}
              alt="Touch Space Bar"
              css={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                animation: `${spacebarGlow} 0.8s ease-in-out infinite`,
              }}
            />
          </Box>
        )}
      </Box>

      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }
      `}</style>
    </Stack>
  );
};
